import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Download, User, Video, Copy, MessageCircle } from 'lucide-react';
import { DOCTOR } from '@/constants/testIds';
import DietPlanEditor from '@/components/diet/DietPlanEditor';

const DoctorAppointment = () => {
  const { apptId } = useParams();
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [notes, setNotes] = useState({ consultation_notes: '', internal_notes: '' });
  const [rx, setRx] = useState({
    items: [{ name: '', dosage: '', duration: '', notes: '' }],
    lifestyle_notes: '',
    dietary_notes: '',
    yoga_recommendations: '',
    panchakarma_recommendations: '',
    notes: '',
    follow_up_date: '',
  });
  const [savedRx, setSavedRx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/doctor/appointments/${apptId}`).then(({ data }) => {
      setData(data);
      setNotes({
        consultation_notes: data.appointment.consultation_notes || '',
        internal_notes: data.appointment.internal_notes || '',
      });
    });
    api.get(`/doctor/appointments/${apptId}/briefing`).then(({ data }) => setBriefing(data)).catch(() => {});
  }, [apptId]);

  const saveNotes = async () => {
    try { await api.put(`/doctor/appointments/${apptId}/notes`, notes); toast.success('Notes saved'); }
    catch { toast.error('Save failed'); }
  };

  const addItem = () => setRx({ ...rx, items: [...rx.items, { name: '', dosage: '', duration: '', notes: '' }] });
  const rmItem = (i) => setRx({ ...rx, items: rx.items.filter((_, k) => k !== i) });
  const updItem = (i, k, v) => {
    const items = [...rx.items];
    items[i] = { ...items[i], [k]: v };
    setRx({ ...rx, items });
  };

  const savePrescription = async () => {
    setSaving(true);
    try {
      const cleaned = { ...rx, appointment_id: apptId, items: rx.items.filter((it) => it.name.trim()) };
      const { data } = await api.post('/doctor/prescriptions', cleaned);
      setSavedRx(data);
      toast.success('Prescription saved and sent to patient');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-sans_mute">Loading…</div>;
  const { appointment: a, patient } = data;
  const patientPhone = (patient?.phone || '').replace(/\D/g, '');
  const waNumber = patientPhone ? (patientPhone.length === 10 ? `91${patientPhone}` : patientPhone) : '';
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi ${patient?.name || ''}, this is ${a.doctor_name} from Saans Ayurved regarding your consultation.`)}` : null;
  const InputCls = 'w-full border border-sans_line bg-white px-3 py-2 text-sm text-sans_ink focus:outline-none focus:border-sans_ink';

  return (
    <div>
      <Link to="/doctor" className="sans-label hover:text-sans_ink">← All appointments</Link>

      <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-sans_ink tracking-tight">{a.user_name}</h1>
          <div className="text-sans_mute mt-2 text-sm">{new Date(a.slot_datetime).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</div>
          <div className="text-sans_mute text-sm">{a.user_email}</div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          {a.meet_link && (
            <a
              href={a.meet_link}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="join-meet-btn"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] bg-sans_ink text-sans_bone px-5 py-3 hover:opacity-90 transition-opacity"
            >
              <Video className="w-4 h-4" strokeWidth={1.75} /> Join meeting
            </a>
          )}
          {a.meet_link && (
            <button
              type="button"
              data-testid="copy-meet-btn"
              onClick={() => {
                navigator.clipboard.writeText(a.meet_link).then(
                  () => toast.success('Meet link copied — paste in SMS or WhatsApp'),
                  () => toast.error('Copy failed'),
                );
              }}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-sans_line text-sans_ink px-5 py-3 hover:bg-sans_line/40 transition-colors"
            >
              <Copy className="w-4 h-4" strokeWidth={1.75} /> Copy link
            </button>
          )}
          <Link to={`/doctor/patients/${a.user_id}`} className="sans-btn-secondary text-xs">
            <User className="w-4 h-4 mr-1" strokeWidth={1.5} /> View patient timeline
          </Link>
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="doctor-whatsapp-btn"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-emerald-600 text-emerald-700 px-4 py-2.5 hover:bg-emerald-600 hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> WhatsApp patient
            </a>
          )}
        </div>
      </div>

      {a.reason && (
        <div className="mt-6 border border-sans_line bg-white p-4 text-sm">
          <div className="sans-label mb-1">Patient reason</div>
          <p className="text-sans_ink">{a.reason}</p>
        </div>
      )}

      {/* Diet planning (only for ₹2,900 diet consultations) */}
      {a.type === 'diet' && (
        <section className="mt-10 border border-sans_line bg-white p-6 md:p-8">
          <div className="sans-label mb-4" style={{ color: '#8A9A5B' }}>₹2,900 Personalised Diet Plan</div>
          <DietPlanEditor appointmentId={apptId} />
        </section>
      )}

      {/* Pre-consultation briefing */}
      {briefing && briefing.prakriti?.has_report && (
        <section className="mt-10 border border-sans_line bg-white p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="sans-label" style={{ color: '#8A9A5B' }}>AI Pre-consultation Briefing</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Read in under a minute</div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 border-b border-sans_line pb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Prakriti</div>
              <div className="font-serif text-xl text-sans_ink mt-1">{briefing.prakriti.primary_dosha} / {briefing.prakriti.secondary_dosha}</div>
              <div className="text-xs text-sans_mute mt-1">V {briefing.prakriti.percentages?.vata}% · P {briefing.prakriti.percentages?.pitta}% · K {briefing.prakriti.percentages?.kapha}% · {briefing.prakriti.confidence}% confidence</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Patient concern</div>
              <div className="text-sans_ink mt-1">{briefing.prakriti.chosen_concern || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">Prior history</div>
              <div className="text-sans_ink mt-1">{briefing.prior_appointments_count} appt · {briefing.prior_prescriptions_count} Rx</div>
            </div>
          </div>

          {/* Clinical snapshot */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {[['Agni', briefing.clinical_snapshot?.agni], ['Ama', briefing.clinical_snapshot?.ama], ['Dhatu', briefing.clinical_snapshot?.dhatu], ['Ojas', briefing.clinical_snapshot?.ojas], ['Dosha interactions', briefing.clinical_snapshot?.dosha_interactions], ['Root cause', briefing.clinical_snapshot?.root_cause]].filter(([, v]) => v && String(v).trim()).map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-sans_mute">{k}</div>
                <p className="text-sans_ink text-sm mt-1 leading-relaxed">{v}</p>
              </div>
            ))}
          </div>

          {(briefing.probable_hidden_symptoms?.length > 0 || briefing.lifestyle_red_flags?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-8 mt-8 pt-6 border-t border-sans_line">
              {briefing.probable_hidden_symptoms?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: '#8B5A3C' }}>Probable hidden symptoms</div>
                  <ul className="mt-3 space-y-1.5">
                    {briefing.probable_hidden_symptoms.map((s, i) => <li key={i} className="text-sm text-sans_ink flex gap-2"><span className="text-sans_mute">—</span>{s}</li>)}
                  </ul>
                </div>
              )}
              {briefing.lifestyle_red_flags?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: '#A88A4E' }}>Lifestyle red flags</div>
                  <ul className="mt-3 space-y-1.5">
                    {briefing.lifestyle_red_flags.map((s, i) => <li key={i} className="text-sm text-sans_ink flex gap-2"><span className="text-sans_mute">—</span>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {briefing.suggested_questions?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-sans_line">
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: '#6E8B6E' }}>Suggested questions for this consultation</div>
              <ol className="mt-3 space-y-2 list-decimal list-inside">
                {briefing.suggested_questions.map((q, i) => <li key={i} className="text-sans_ink leading-relaxed">{q}</li>)}
              </ol>
            </div>
          )}

          {briefing.risk_areas?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-sans_line">
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: '#7A4E2D' }}>Risk areas to probe deeper</div>
              <ul className="mt-3 space-y-1.5">
                {briefing.risk_areas.map((s, i) => <li key={i} className="text-sm text-sans_ink flex gap-2"><span className="text-sans_mute">—</span>{s}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-6 text-[10px] text-sans_mute italic">Summary generated from patient's Prakriti + history. Apply your independent clinical judgment.</div>
        </section>
      )}

      {/* Consultation notes */}
      <section className="mt-10">
        <div className="sans-label mb-4">Consultation notes (visible to patient)</div>
        <textarea rows={4} value={notes.consultation_notes} onChange={(e) => setNotes({ ...notes, consultation_notes: e.target.value })} className={InputCls} placeholder="What you discussed with the patient…" />
        <div className="sans-label mt-6 mb-4">Internal notes (only visible to you)</div>
        <textarea rows={3} value={notes.internal_notes} onChange={(e) => setNotes({ ...notes, internal_notes: e.target.value })} className={InputCls} placeholder="Observations, red flags, follow-up cues…" />
        <button onClick={saveNotes} className="sans-btn-secondary mt-4 text-xs">Save notes</button>
      </section>

      {/* Prescription builder */}
      <section className="mt-14">
        <div className="flex items-center justify-between mb-4">
          <div className="sans-label">Prescription builder</div>
          {savedRx && (
            <a href={`${API_BASE}/doctor/prescriptions/${savedRx.prescription_id}/pdf`} target="_blank" rel="noreferrer" data-testid={DOCTOR.downloadPdfBtn} className="sans-btn-secondary text-xs">
              <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> Download PDF
            </a>
          )}
        </div>

        <div className="bg-white border border-sans_line">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-sans_line text-[10px] tracking-[0.18em] uppercase text-sans_mute">
            <div className="col-span-4">Formulation</div>
            <div className="col-span-3">Dosage</div>
            <div className="col-span-2">Duration</div>
            <div className="col-span-2">Notes</div>
            <div className="col-span-1"></div>
          </div>
          {rx.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-sans_line last:border-0 items-center">
              <input className={`${InputCls} col-span-4`} placeholder="e.g. Triphala Churna" value={it.name} onChange={(e) => updItem(i, 'name', e.target.value)} />
              <input className={`${InputCls} col-span-3`} placeholder="1 tsp / twice daily" value={it.dosage} onChange={(e) => updItem(i, 'dosage', e.target.value)} />
              <input className={`${InputCls} col-span-2`} placeholder="30 days" value={it.duration} onChange={(e) => updItem(i, 'duration', e.target.value)} />
              <input className={`${InputCls} col-span-2`} placeholder="with warm water" value={it.notes} onChange={(e) => updItem(i, 'notes', e.target.value)} />
              <button onClick={() => rmItem(i)} className="col-span-1 text-sans_mute hover:text-red-600 justify-self-end" aria-label="Remove"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          ))}
          <div className="p-3">
            <button onClick={addItem} data-testid={DOCTOR.addRxItemBtn} className="text-xs text-sans_ink border border-sans_line px-3 py-2 hover:bg-sans_bone inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add formulation
            </button>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div>
            <label className="sans-label block mb-2">Lifestyle recommendations</label>
            <textarea rows={3} className={InputCls} value={rx.lifestyle_notes} onChange={(e) => setRx({ ...rx, lifestyle_notes: e.target.value })} />
          </div>
          <div>
            <label className="sans-label block mb-2">Dietary recommendations</label>
            <textarea rows={3} className={InputCls} value={rx.dietary_notes} onChange={(e) => setRx({ ...rx, dietary_notes: e.target.value })} />
          </div>
          <div>
            <label className="sans-label block mb-2">Yoga recommendations</label>
            <textarea rows={3} className={InputCls} value={rx.yoga_recommendations} onChange={(e) => setRx({ ...rx, yoga_recommendations: e.target.value })} />
          </div>
          <div>
            <label className="sans-label block mb-2">Panchakarma recommendations</label>
            <textarea rows={3} className={InputCls} value={rx.panchakarma_recommendations} onChange={(e) => setRx({ ...rx, panchakarma_recommendations: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="sans-label block mb-2">Additional notes</label>
            <textarea rows={2} className={InputCls} value={rx.notes} onChange={(e) => setRx({ ...rx, notes: e.target.value })} />
          </div>
          <div>
            <label className="sans-label block mb-2">Suggested follow-up date</label>
            <input type="date" className={InputCls} value={rx.follow_up_date || ''} onChange={(e) => setRx({ ...rx, follow_up_date: e.target.value })} />
          </div>
        </div>

        <button onClick={savePrescription} disabled={saving} data-testid={DOCTOR.savePrescriptionBtn} className="sans-btn-primary mt-8">
          {saving ? 'Saving…' : 'Save prescription & notify patient'}
        </button>
      </section>
    </div>
  );
};

export default DoctorAppointment;
